import { StandardEvents } from '@shopify/events';

// Shopify's legacy load-time form scan treats every /cart/add form as a
// product view, using the current page's product metadata. Cross-sells are
// not PDP views: keep their native forms inert until explicit add intent,
// after all initial load listeners. Actual adds retain native AJAX tracking.
const afterInitialLoad = new Promise((resolve) => {
  const finish = () => setTimeout(resolve, 0);
  if (document.readyState === 'complete') finish();
  else window.addEventListener('load', finish, { once: true });
});

class CartRecommendation extends HTMLElement {
  #pending = false;

  connectedCallback() {
    this.querySelector('[data-ct-start]')?.setAttribute('role', 'button');
    this.addEventListener('click', this.#activate);
    this.addEventListener('keydown', this.#activate);
    this.addEventListener('keyup', this.#activate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#activate);
    this.removeEventListener('keydown', this.#activate);
    this.removeEventListener('keyup', this.#activate);
  }

  #activate = async (event) => {
    const trigger = event.target.closest?.('[data-ct-start]');
    if (!trigger || !this.contains(trigger)) return;
    if (event.type !== 'click' && event.key !== ' ') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // If native buying components are unavailable, the link still opens PDP.
    if (!customElements.get('product-form-component') || !customElements.get('add-to-cart-component')) return;
    const template = this.querySelector('template');
    if (!template) return;
    event.preventDefault();
    // The trigger is replaced below. Do not let the drawer's backdrop
    // detector inspect that detached click target and close the panel.
    event.stopPropagation();
    // Activate Space on release, like a native button. Mounting/focusing on
    // keydown could make keyup submit the replacement button a second time.
    if (event.type === 'keydown') return;
    if (this.#pending) return;
    this.#pending = true;
    this.setAttribute('aria-busy', 'true');
    trigger.setAttribute('aria-disabled', 'true');
    await afterInitialLoad;
    if (!this.isConnected) {
      this.#pending = false;
      this.removeAttribute('aria-busy');
      trigger.removeAttribute('aria-disabled');
      return;
    }
    const retainFocus = document.activeElement === trigger;
    this.replaceChildren(template.content.cloneNode(true));
    this.removeAttribute('aria-busy');
    const button = this.querySelector('button[type="submit"]');
    if (retainFocus) button?.focus({ preventScroll: true });
    // Preserve native submission and validation without a second click event.
    button?.form?.requestSubmit(button);
    this.#pending = false;
  };
}

if (!customElements.get('ct-cart-recommendation')) {
  customElements.define('ct-cart-recommendation', CartRecommendation);
}

// Native product forms remain the only writer. Lock repeat clicks until their
// existing cart promise settles, including failure; no extra cart request.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const form = event.target.closest?.('product-form-component[data-ct-cart-add]');
  if (!form || !event.promise) return;
  const buttons = [...form.querySelectorAll('button[type="submit"]')];
  form.setAttribute('aria-busy', 'true');
  buttons.forEach((button) => { button.disabled = true; });
  const unlock = () => {
    form.removeAttribute('aria-busy');
    buttons.forEach((button) => { button.disabled = false; });
  };
  event.promise.then(unlock, unlock);
});
